import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError, formatDoctorName } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { ChevronLeft, ChevronRight, Calendar, Clock, CheckCircle, UserCheck } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const STATUS_COLORS = {
  waiting: "bg-amber-400",
  in_consultation: "bg-blue-500",
  completed: "bg-emerald-500",
  cancelled: "bg-rose-400",
};

const STATUS_BADGE = {
  waiting: "bg-amber-100 text-amber-800",
  in_consultation: "bg-blue-100 text-blue-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-800",
};

export default function AppointmentCalendar() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);

  const fetchAppointments = useCallback(async () => {
    try {
      const y = currentDate.getFullYear();
      const m = currentDate.getMonth() + 1;
      const { data } = await api.get(`/appointments/calendar?year=${y}&month=${m}`);
      setAppointments(data.data);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setLoading(false); }
  }, [currentDate, toast]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  const prevMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToday = () => { setCurrentDate(new Date()); setSelectedDate(new Date().toISOString().split("T")[0]); };

  // Build calendar grid
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  const getDateStr = (day) => day ? `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` : null;
  const getApptsForDay = (day) => {
    if (!day) return [];
    const dateStr = getDateStr(day);
    return appointments.filter(a => a.visit_date?.startsWith(dateStr));
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  const isSelected = (day) => day && getDateStr(day) === selectedDate;

  const selectedAppts = selectedDate ? appointments.filter(a => a.visit_date?.startsWith(selectedDate)) : [];

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/appointments/${id}/status`, { status });
      fetchAppointments();
      toast({ title: `Status updated to ${status}` });
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  // Weekly view: get days of current week
  const getWeekDays = () => {
    const d = new Date(currentDate);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    return Array.from({ length: 7 }, (_, i) => {
      const wd = new Date(d);
      wd.setDate(d.getDate() + i);
      return wd;
    });
  };
  const weekDays = getWeekDays();
  const prevWeek = () => setCurrentDate(d => { const nd = new Date(d); nd.setDate(nd.getDate() - 7); return nd; });
  const nextWeek = () => setCurrentDate(d => { const nd = new Date(d); nd.setDate(nd.getDate() + 7); return nd; });

  const monthStats = {
    total: appointments.length,
    waiting: appointments.filter(a => a.status === "waiting").length,
    completed: appointments.filter(a => a.status === "completed").length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">Appointment Calendar</h1>
          <p className="text-slate-500 text-sm mt-1">{MONTHS[month]} {year} — {monthStats.total} appointments</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1">
            <Button variant={viewMode === "month" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("month")} className={viewMode === "month" ? "bg-white shadow-sm text-slate-900 h-8" : "h-8 text-slate-600"} data-testid="month-view-btn">Month</Button>
            <Button variant={viewMode === "week" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("week")} className={viewMode === "week" ? "bg-white shadow-sm text-slate-900 h-8" : "h-8 text-slate-600"} data-testid="week-view-btn">Week</Button>
          </div>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="today-btn">Today</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "This Month", value: monthStats.total, icon: Calendar, color: "text-slate-700", bg: "bg-slate-50" },
          { label: "Waiting", value: monthStats.waiting, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
          { label: "Completed", value: monthStats.completed, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className={`${bg} rounded-xl p-4 flex items-center gap-3`}>
            <Icon size={20} className={color} />
            <div>
              <p className={`text-2xl font-bold font-sans ${color}`}>{value}</p>
              <p className="text-xs text-slate-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Calendar Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <Button variant="ghost" size="sm" onClick={viewMode === "month" ? prevMonth : prevWeek} className="h-8 w-8 p-0" data-testid="prev-btn">
              <ChevronLeft size={16} />
            </Button>
            <h2 className="text-base font-bold text-slate-900 font-sans">
              {viewMode === "month" ? `${MONTHS[month]} ${year}` : `${weekDays[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
            </h2>
            <Button variant="ghost" size="sm" onClick={viewMode === "month" ? nextMonth : nextWeek} className="h-8 w-8 p-0" data-testid="next-btn">
              <ChevronRight size={16} />
            </Button>
          </div>

          {viewMode === "month" ? (
            /* Month View */
            <div>
              {/* Day headers */}
              <div className="grid grid-cols-7 bg-slate-50">
                {DAYS.map(d => <div key={d} className="py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">{d}</div>)}
              </div>
              {/* Calendar cells */}
              <div>
                {weeks.map((week, wi) => (
                  <div key={wi} className="grid grid-cols-7 border-t border-slate-100">
                    {week.map((day, di) => {
                      const appts = getApptsForDay(day);
                      return (
                        <div
                          key={di}
                          onClick={() => day && setSelectedDate(getDateStr(day))}
                          className={`min-h-[70px] p-2 border-r border-slate-100 last:border-r-0 cursor-pointer transition-colors hover:bg-slate-50 ${isSelected(day) ? "bg-emerald-50 border-l-2 border-l-emerald-500" : ""} ${!day ? "bg-slate-50/50" : ""}`}
                          data-testid={day ? `calendar-day-${getDateStr(day)}` : undefined}
                        >
                          {day && (
                            <>
                              <span className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full ${isToday(day) ? "bg-emerald-700 text-white" : isSelected(day) ? "text-emerald-700" : "text-slate-700"}`}>
                                {day}
                              </span>
                              <div className="mt-1 space-y-0.5">
                                {appts.slice(0, 2).map((a, i) => (
                                  <div key={i} className="flex items-center gap-1">
                                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_COLORS[a.status] || "bg-slate-400"}`} />
                                    <span className="text-[10px] text-slate-600 truncate leading-tight">{a.patient_name?.split(" ")[0]}</span>
                                  </div>
                                ))}
                                {appts.length > 2 && <span className="text-[10px] text-slate-400">+{appts.length - 2} more</span>}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Week View */
            <div>
              <div className="grid grid-cols-7 bg-slate-50">
                {weekDays.map((wd, i) => {
                  const isWdToday = wd.toDateString() === new Date().toDateString();
                  return (
                    <div key={i} className={`py-3 text-center cursor-pointer hover:bg-slate-100 transition-colors ${isWdToday ? "bg-emerald-50" : ""}`} onClick={() => setSelectedDate(wd.toISOString().split("T")[0])}>
                      <p className="text-xs text-slate-500 uppercase">{DAYS[wd.getDay()]}</p>
                      <p className={`text-sm font-bold mt-0.5 w-7 h-7 flex items-center justify-center rounded-full mx-auto ${isWdToday ? "bg-emerald-700 text-white" : "text-slate-700"}`}>{wd.getDate()}</p>
                      {(() => {
                        const cnt = appointments.filter(a => a.visit_date?.startsWith(wd.toISOString().split("T")[0])).length;
                        return cnt > 0 ? <span className="text-xs text-emerald-600 font-semibold">{cnt}</span> : null;
                      })()}
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-7 divide-x divide-slate-100 min-h-48">
                {weekDays.map((wd, i) => {
                  const dateStr = wd.toISOString().split("T")[0];
                  const dayAppts = appointments.filter(a => a.visit_date?.startsWith(dateStr));
                  return (
                    <div key={i} className="p-2 space-y-1">
                      {dayAppts.map(a => (
                        <div key={a.id} onClick={() => setSelectedAppt(a)} className={`p-1.5 rounded-lg cursor-pointer text-[10px] leading-tight ${STATUS_BADGE[a.status] || "bg-slate-100 text-slate-700"} hover:opacity-80 transition-opacity`}>
                          <p className="font-semibold truncate">#{a.token_number} {a.patient_name?.split(" ")[0]}</p>
                          <p className="truncate opacity-75">{formatDoctorName(a.doctor_name)}</p>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Selected Day Appointments */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-slate-100 flex-shrink-0">
            <h3 className="font-semibold text-slate-900 text-sm font-sans">
              {selectedDate ? new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : "Select a date"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{selectedAppts.length} appointments</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {!selectedDate ? (
              <div className="py-12 text-center text-slate-400 text-sm">
                <Calendar size={28} className="mx-auto mb-2 opacity-30" />
                Click on a date to see appointments
              </div>
            ) : selectedAppts.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm">
                <Clock size={28} className="mx-auto mb-2 opacity-30" />
                No appointments on this date
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {selectedAppts.map(appt => (
                  <div key={appt.id} className="px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedAppt(appt)} data-testid={`cal-appt-${appt.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-2 h-2 rounded-full ${STATUS_COLORS[appt.status]}`} />
                      <span className="text-xs font-bold text-slate-700">#{appt.token_number}</span>
                      {appt.is_emergency && <Badge className="bg-rose-100 text-rose-700 text-[10px] py-0">Emergency</Badge>}
                    </div>
                    <p className="text-sm font-semibold text-slate-900 leading-tight">{appt.patient_name}</p>
                    <p className="text-xs text-slate-500">{formatDoctorName(appt.doctor_name)}</p>
                    <p className="text-xs text-slate-500">{appt.visit_type} • {appt.complaint || "—"}</p>
                    <Badge className={`text-[10px] mt-1 ${STATUS_BADGE[appt.status]}`}>{appt.status?.replace("_", " ")}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap">
        <span className="text-xs text-slate-500 font-medium">Status:</span>
        {Object.entries(STATUS_COLORS).map(([status, cls]) => (
          <div key={status} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${cls}`} />
            <span className="text-xs text-slate-600 capitalize">{status.replace("_", " ")}</span>
          </div>
        ))}
      </div>

      {/* Appointment Detail Modal */}
      <Dialog open={!!selectedAppt} onOpenChange={() => setSelectedAppt(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-sans">Appointment #{selectedAppt?.token_number}</DialogTitle>
          </DialogHeader>
          {selectedAppt && (
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-slate-500">Patient</p><p className="font-medium">{selectedAppt.patient_name}</p></div>
                <div><p className="text-xs text-slate-500">Doctor</p><p className="font-medium">{formatDoctorName(selectedAppt.doctor_name)}</p></div>
                <div><p className="text-xs text-slate-500">Type</p><p className="font-medium">{selectedAppt.visit_type}</p></div>
                <div><p className="text-xs text-slate-500">Status</p><Badge className={`text-xs ${STATUS_BADGE[selectedAppt.status]}`}>{selectedAppt.status?.replace("_", " ")}</Badge></div>
                {selectedAppt.complaint && <div className="col-span-2"><p className="text-xs text-slate-500">Complaint</p><p className="font-medium">{selectedAppt.complaint}</p></div>}
              </div>
              {selectedAppt.status === "waiting" && (
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={() => { updateStatus(selectedAppt.id, "in_consultation"); setSelectedAppt(null); }} data-testid="cal-checkin-btn">Check In</Button>
              )}
              {selectedAppt.status === "in_consultation" && (
                <Button className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => { updateStatus(selectedAppt.id, "completed"); setSelectedAppt(null); }} data-testid="cal-complete-btn">Mark Completed</Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
